import React, { useEffect, useRef, useState } from 'react';
import './LoginOrSignup.scss';
import GoogleIcon from './GoogleIcon';
import FacebookIcon from './FacebookIcon';
import { getCookie } from '../utils/utils';
import TagManager from 'react-gtm-module';
import { Trans, useTranslation } from 'react-i18next';
import ListingCountUp from './ListingCountUp';
import { apiRequest } from '../utils/api';

declare global {
  interface Window {
    google: any;
    FB: any;
    fbAsyncInit: () => void;
  }
}

type Props = {
  email: string;
  setShowLoginView: (showLoginView: boolean) => void;
  setShowSignupView: (showSignupView: boolean) => void;
  toggleSignupLoginModalVisible: () => void;
  validateEmail: (email: string) => string;
  onLoginSuccess: (expires_in: number, marketing_consent_shown: boolean) => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setEmail: (email: string) => void;
  setResponseEmail: (email: string) => void;
  authError?: string;
  setAuthError?: (error: string) => void;
};

/**
 * Props for the marketing panel beside the form. `variant` picks which
 * breakpoint's copy and layout the panel renders, since the mobile and desktop
 * placements show the same pitch in different shapes.
 */
type InfoPanelProps = {
  variant: 'mobile' | 'desktop';
  listingTotal: number;
};

/**
 * Value-proposition panel that sits under the form. The markup carries one copy
 * for narrow viewports and one for 768px and up; both render the same copy, so
 * the only difference is which breakpoint reveals it.
 *
 * The headline counts up to Jaakko's live listing total, and a customer quote
 * closes the panel where a product photo used to sit: the photo said nothing the
 * headline did not, and the quote carries the commission saving.
 */
const InfoPanel = ({ variant, listingTotal }: InfoPanelProps) => (
  <div className={`login-or-signup__info login-or-signup__info--${variant}`}>
    <div className="login-or-signup__info-title">
      <img src="/assets/success.png" alt="" width={50} height={50} />
      <span className="login-or-signup__info-title-text">
        <Trans
          i18nKey="signupLoginModal.infoBox.title"
          components={{
            count: <ListingCountUp target={listingTotal} />,
            tail: <span className="login-or-signup__info-title-tail" />,
          }}
        />
      </span>
    </div>
    <div className="login-or-signup__info-list">
      <div className="login-or-signup__info-item">
        <span className="login-or-signup__info-marker" />
        <span className="login-or-signup__info-claim">
          <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.freeAccount" />
        </span>
      </div>
      <div className="login-or-signup__info-item">
        <span className="login-or-signup__info-marker" />
        <span className="login-or-signup__info-claim">
          <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.noFees" />
        </span>
      </div>
      <div className="login-or-signup__info-item">
        <span className="login-or-signup__info-marker" />
        <span className="login-or-signup__info-claim">
          <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.anywhere" />
        </span>
      </div>
    </div>
    <div className="login-or-signup__info-testimonial">
      <blockquote className="login-or-signup__info-quote">
        <Trans i18nKey="signupLoginModal.infoBox.testimonial.quote" />
      </blockquote>
      <div className="login-or-signup__info-author">
        <Trans i18nKey="signupLoginModal.infoBox.testimonial.author" />
      </div>
      <div className="login-or-signup__info-saving">
        <Trans i18nKey="signupLoginModal.infoBox.testimonial.saving" />
      </div>
    </div>
  </div>
);

// Shown until GET /listings/count answers, and kept if it never does: the
// number the panel promised before it was live.
const FALLBACK_LISTING_TOTAL = 1200;

/**
 * First screen of the auth funnel: Google and Facebook login, plus the
 * email-first branch that asks /check-if-user-exists and routes to either the
 * password or the signup screen. Renders the screen body only; the overlay,
 * the sheet and the header row belong to the SignupLoginModal shell.
 */
const LoginOrSignup = (props: Props) => {
  const {
    email,
    setEmail,
    setShowLoginView,
    setShowSignupView,
    toggleSignupLoginModalVisible,
    validateEmail,
    onLoginSuccess,
    setIsLoggedIn,
    setResponseEmail,
  } = props;
  const { t, i18n } = useTranslation();
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [emailError, setEmailError] = useState('');
  const languageIsEnglish = i18n.language.startsWith('en');
  const [listingTotal, setListingTotal] = useState(FALLBACK_LISTING_TOTAL);

  useEffect(() => {
    let cancelled = false;
    apiRequest('/listings/count')
      .then((data: { total?: number }) => {
        if (!cancelled && typeof data?.total === 'number' && data.total > 0) {
          setListingTotal(data.total);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Use props version if provided, otherwise use local state
  const [localAuthError, setLocalAuthError] = useState('');
  const authError = props.authError !== undefined ? props.authError : localAuthError;
  const setAuthError = props.setAuthError || setLocalAuthError;

  // Tears down the active Google-login poller + listeners if the modal unmounts.
  const googleLoginCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    initializeFacebookSDK();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => googleLoginCleanupRef.current?.(), []);

  const initializeFacebookSDK = () => {
    const init = () => {
      window.FB.init({
        appId: process.env.REACT_APP_FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
    };

    if (window.FB) {
      init();
    } else {
      window.fbAsyncInit = init;
      // Optionally, poll for FB in case fbAsyncInit is missed
      const interval = setInterval(() => {
        if (window.FB) {
          init();
          clearInterval(interval);
        }
      }, 100);
    }
  };

  const handleSubmitCheckIfUserExists = async () => {
    setIsCheckingUser(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check-if-user-exists?email=${email.toLowerCase()}`,
        {
          method: 'GET',
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          setShowLoginView(true);
        } else {
          setShowSignupView(true);
        }
      } else {
        const data = await response.json();
        if (data.error === 'exists_with_another_authentication_method') {
          setAuthError(t('signupLoginModal.errors.existsWithAnotherAuthenticationMethod'));
        } else {
          // Generic error for all other failures
          setAuthError(t('signupLoginModal.errors.emailCheckFailed'));
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsCheckingUser(false);
    }
  };

  // Start the self-managed Google popup login. The GIS popup (initCodeClient) is
  // unreliable on iOS WebKit, which nulls window.opener and drops the popup's
  // cross-origin postMessage handback, so the auth code never reached the SPA. The
  // SPA opens the backend /auth/google/start URL and detects the outcome by polling
  // /check-login-status AND re-checking whenever this tab regains focus, so it works
  // even when Google takes several minutes, never unmounts the SPA, and never relies
  // on window.opener. Success, a failure (g_oauth_result cookie), an unmount, or a
  // generous overall cap all tear the poller down.
  const handleGoogleButtonClick = () => {
    setAuthError('');
    TagManager.dataLayer({
      dataLayer: {
        event: 'google_login_or_signup_button_clicked',
        category: 'User Interaction',
        action: 'Click',
        label: 'Google Login or Signup Button',
      },
    });

    // iOS only grants a real window from a synchronous gesture, so open before any await.
    const popupW = 480;
    const popupH = 640;
    const popupLeft = window.screenX + Math.max(0, (window.outerWidth - popupW) / 2);
    const popupTop = window.screenY + Math.max(0, (window.outerHeight - popupH) / 2);
    const popup = window.open(
      `${process.env.REACT_APP_API_URL}/auth/google/start`,
      'jaakko-google-auth',
      `width=${popupW},height=${popupH},left=${popupLeft},top=${popupTop}`,
    );
    if (!popup) {
      // Popup blocked: nothing to poll, so surface the error instead of spinning.
      setAuthError(t('signupLoginModal.errors.genericOAuth'));
      return;
    }

    let settled = false;
    const startedAt = Date.now();
    const MAX_MS = 10 * 60 * 1000;
    const POLL_MS = 1000;

    const stop = () => {
      settled = true;
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      googleLoginCleanupRef.current = null;
      try {
        popup?.close();
      } catch (e) {
        // iOS may refuse to close the tab; harmless.
      }
    };
    googleLoginCleanupRef.current = stop;

    const check = async () => {
      if (settled) return;
      if (Date.now() - startedAt > MAX_MS) {
        stop();
        return;
      }
      let data: any = null;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/check-login-status`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') },
        });
        if (res.ok) {
          data = await res.json();
        }
      } catch (e) {
        // Network blip; fall through to the cookie check and let the next tick retry.
      }

      if (data && data.isLoggedIn && typeof data.expires_in === 'number') {
        if (settled) return;
        stop();
        setAuthError('');
        setIsLoggedIn(true);
        if (data.email) {
          setResponseEmail(data.email);
        }
        onLoginSuccess(data.expires_in, data.marketing_consent_shown ?? true);
        TagManager.dataLayer({
          dataLayer: {
            event: 'login_by_google_success',
            category: 'User Interaction',
            action: 'Success',
            label: 'Login by Google Success',
            userId: data.email?.toLowerCase(),
          },
        });
        toggleSignupLoginModalVisible();
        return;
      }

      // A logged-out result (including the 401 returned when no session exists yet)
      // is the failure case — surface any g_oauth_result the backend set.
      const result = getCookie('g_oauth_result');
      if (result) {
        if (settled) return;
        stop();
        // Consume the one-shot failure cookie so a later retry can't read this stale error.
        const domain = window.location.hostname.split('.').slice(-2).join('.');
        document.cookie = 'g_oauth_result=; Max-Age=0; path=/';
        document.cookie = `g_oauth_result=; Max-Age=0; path=/; domain=.${domain}`;
        setAuthError(
          result === 'exists_with_another_authentication_method'
            ? t('signupLoginModal.errors.existsWithAnotherAuthenticationMethod')
            : t('signupLoginModal.errors.genericOAuth'),
        );
      }
    };

    // Re-check whenever this tab regains focus: covers iOS (auth opens as a separate tab) and slow logins the interval might miss.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    const poll = setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
  };

  const handleFacebookButtonClick = () => {
    setAuthError(''); // Clear any existing errors
    TagManager.dataLayer({
      dataLayer: {
        event: 'facebook_login_or_signup_button_clicked',
        category: 'User Interaction',
        action: 'Click',
        label: 'Facebook Login or Signup Button',
      },
    });

    window.FB.login((response: any) => {
      if (response.authResponse) {
        // Send the access token to your backend
        fetch(`${process.env.REACT_APP_API_URL}/verify-facebook-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            accessToken: response.authResponse.accessToken,
            language: i18n.language,
            user_agent: navigator.userAgent
          }),
          credentials: 'include',
        })
          .then((res) => res.json())
          .then((data) => {
            const resolvedEmail =
              data?.email || data?.user?.email || data?.user_email || data?.userEmail || '';

            if (data.error) {
              if (data.error === 'exists_with_another_authentication_method') {
                setAuthError(t('signupLoginModal.errors.existsWithAnotherAuthenticationMethod'));
              } else {
                setAuthError(t('signupLoginModal.errors.genericOAuth'));
              }
            } else {
              setAuthError('');
              setIsLoggedIn(true);
              if (resolvedEmail) {
                setResponseEmail(resolvedEmail);
              }
              onLoginSuccess(data.expires_in, data.marketing_consent_shown ?? true);
              TagManager.dataLayer({
                dataLayer: {
                  event: 'login_by_facebook_success',
                  category: 'User Interaction',
                  action: 'Success',
                  label: 'Login by Facebook Success',
                  userId: data.email?.toLowerCase(),
                },
              });
              toggleSignupLoginModalVisible();
            }
          })
          .catch((error) => {
            setAuthError(t('signupLoginModal.errors.networkError'));
          });
      } else {
        console.log('User cancelled login or did not fully authorize.');
      }
    }, { scope: 'public_profile,email' });
  };

  return (
    <div className="login-or-signup">
      <div className="login-or-signup__intro">
        <h2 className="login-or-signup__heading">
          <Trans i18nKey="signupLoginModal.loginOrSignup" />
        </h2>
        {authError && <p className="login-or-signup__error">{authError}</p>}
      </div>
      <div className="login-or-signup__socials">
        <button
          id="custom-google-signin-button"
          className="login-or-signup__social"
          onClick={handleGoogleButtonClick}
        >
          <GoogleIcon />
          <Trans i18nKey="signupLoginModal.continueWithGoogle" />
        </button>
        <button
          id="custom-facebook-signin-button"
          className="login-or-signup__social"
          onClick={handleFacebookButtonClick}
        >
          <FacebookIcon />
          <Trans i18nKey="signupLoginModal.continueWithFacebook" />
        </button>
      </div>
      <div className="login-or-signup__divider">
        <Trans i18nKey="signupLoginModal.orContinueWithEmail" />
      </div>
      <form
        className="login-or-signup__form"
        onSubmit={(e) => {
          e.preventDefault();
          setAuthError('');
          const emailValidationError = validateEmail(email);
          setEmailError(emailValidationError);
          if (!emailValidationError) {
            handleSubmitCheckIfUserExists();
          }
        }}
      >
        <div className="login-or-signup__field">
          <input
            type="email"
            placeholder={t('signupLoginModal.emailPlaceholder')}
            className={`login-or-signup__input ${
              emailError ? 'login-or-signup__input--invalid' : ''
            }`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          {emailError && <p className="login-or-signup__error">{emailError}</p>}
        </div>
        <button
          type="submit"
          className={`login-or-signup__cta ${
            isCheckingUser ? 'login-or-signup__cta--loading' : ''
          }`}
          disabled={isCheckingUser}
        >
          {isCheckingUser ? (
            <span className="loading-dots">
              <span></span>
            </span>
          ) : (
            <Trans i18nKey="signupLoginModal.loginOrSignupButton" />
          )}
        </button>
      </form>
      <p className="login-or-signup__legal">
        <Trans i18nKey="signupLoginModal.registerText.first" />
        <a
          href={
            languageIsEnglish
              ? 'https://info.jaakko.com/en-gb/terms_and_conditions'
              : 'https://info.jaakko.com/kayttoehdot'
          }
          target="_blank"
          rel="noreferrer"
        >
          <Trans i18nKey="signupLoginModal.registerText.second" />
        </a>
        <Trans i18nKey="signupLoginModal.registerText.third" />
        <a
          href={
            languageIsEnglish
              ? 'https://info.jaakko.com/en-gb/privacy_policy'
              : 'https://info.jaakko.com/privacy_policy'
          }
          target="_blank"
          rel="noreferrer"
        >
          <Trans i18nKey="signupLoginModal.registerText.fourth" />
        </a>
      </p>
      <InfoPanel variant="desktop" listingTotal={listingTotal} />
      <InfoPanel variant="mobile" listingTotal={listingTotal} />
    </div>
  );
};

export default LoginOrSignup;
